/**
 * Pure helpers for the agent chat: merging events by seq, deriving plan state from events, and
 * grouping events into thread rows.
 */
import {
  AgentConversation, AgentEvent, AgentPlan, AgentPlanPayload, AgentPreview,
} from '../../../types/interfaces';

/** Plan row plus what only events tell: an execution whose outcome is unknown (R33/R34). */
export type PlanState = AgentPlan & { outcome?: 'unknown' };

export type PlanMap = Record<number, PlanState>;

/** The worker's R33 reconciliation text (agent/worker.py ERR_OUTCOME_UNKNOWN). */
export const ERR_OUTCOME_UNKNOWN = 'Execution was interrupted; the outcome is unknown. '
  + 'Check the audit log and the data before retrying.';

/** Events in seq order, without duplicate seqs. Returns `current` itself when nothing is new. */
export function mergeEvents(current: AgentEvent[], incoming: AgentEvent[]): AgentEvent[] {
  const seen = new Set(current.map((e) => e.seq));
  const fresh = incoming.filter((e) => {
    if (seen.has(e.seq)) return false;
    seen.add(e.seq);
    return true;
  });
  if (fresh.length === 0) return current;
  const last = current.length ? current[current.length - 1].seq : -Infinity;
  const merged = current.concat(fresh);
  return fresh.every((e) => e.seq > last) && isSorted(fresh) ? merged : merged.sort((a, b) => a.seq - b.seq);
}

function isSorted(events: AgentEvent[]): boolean {
  return events.every((e, i) => i === 0 || events[i - 1].seq < e.seq);
}

export function lastSeq(events: AgentEvent[]): number {
  return events.length ? events[events.length - 1].seq : 0;
}

function skeleton(id: number): PlanState {
  return {
    id, message_id: null, plan: { summary: '', steps: [], skipped_inputs: [] }, preview: null,
    fingerprint: null, status: 'previewing', approved_by: null, approved_at: null,
    preview_built_at: null, created_at: '',
  };
}

/**
 * Apply one event to the plan map. Plan state comes from the latest `plan` and `status` events
 * for each plan_id, plus `result` (and an `error` naming a plan, which only reconciliation sends).
 */
export function applyEvent(plans: PlanMap, event: AgentEvent): PlanMap {
  const p = event.payload || {};
  const planId: number | undefined = typeof p.plan_id === 'number' ? p.plan_id : undefined;
  if (planId === undefined) return plans;
  const current = plans[planId] || skeleton(planId);
  let next: PlanState;
  switch (event.type) {
    case 'plan': {
      const payload = p as AgentPlanPayload;
      next = {
        ...current, plan: payload.plan, preview: payload.preview as AgentPreview,
        fingerprint: payload.fingerprint, status: 'awaiting_approval', approved_by: null, approved_at: null,
        outcome: undefined,
      };
      break;
    }
    case 'status':
      next = { ...current, status: p.state, ...(p.state === 'approved' && p.by ? { approved_by: p.by } : {}) };
      break;
    case 'result':
      next = {
        ...current, status: p.status === 'done' ? 'done' : 'failed',
        outcome: p.status === 'unknown' ? 'unknown' : undefined,
      };
      break;
    case 'error':
      next = {
        ...current, status: 'failed',
        ...(String(p.text ?? '').trim() === ERR_OUTCOME_UNKNOWN ? { outcome: 'unknown' as const } : {}),
      };
      break;
    default:
      return plans;
  }
  return { ...plans, [planId]: next };
}

/**
 * Plan state for a freshly loaded conversation: derived from its events, then overlaid with the
 * plan rows, which are at least as new as every event in the same response.
 */
export function plansFromConversation(conversation: AgentConversation): PlanMap {
  return overlayRows(conversation.events.reduce(applyEvent, {} as PlanMap), conversation.plans);
}

/** Overlay plan rows on event-derived state; an unknown outcome survives only on a failed row. */
export function overlayRows(plans: PlanMap, rows: AgentPlan[]): PlanMap {
  let next = plans;
  rows.forEach((row) => {
    const outcome = row.status === 'failed' ? next[row.id]?.outcome : undefined;
    next = { ...next, [row.id]: { ...next[row.id], ...row, outcome } };
  });
  return next;
}

/** One rendered row of the thread. */
export type ThreadItem =
  | { kind: 'event'; event: AgentEvent }
  | { kind: 'tools'; key: number; events: AgentEvent[] }
  | { kind: 'plan'; event: AgentEvent; planId: number; reapproval: { reason: string | null } | null };

const HIDDEN = new Set(['status', 'done']);

/**
 * Group events into rows: consecutive tool_call/tool_result events collapse into one row; each
 * plan is shown once, at its latest `plan` event; status and done events are not rows.
 */
export function threadItems(events: AgentEvent[]): ThreadItem[] {
  const latestPlanSeq: Record<number, number> = {};
  events.forEach((e) => {
    if (e.type === 'plan' && typeof e.payload?.plan_id === 'number') latestPlanSeq[e.payload.plan_id] = e.seq;
  });
  const items: ThreadItem[] = [];
  events.forEach((e) => {
    if (HIDDEN.has(e.type)) return;
    if (e.type === 'tool_call' || e.type === 'tool_result') {
      const prev = items[items.length - 1];
      if (prev && prev.kind === 'tools') prev.events.push(e);
      else items.push({ kind: 'tools', key: e.seq, events: [e] });
      return;
    }
    if (e.type === 'plan') {
      const planId = e.payload?.plan_id;
      if (typeof planId !== 'number' || latestPlanSeq[planId] !== e.seq) return;
      items.push({
        kind: 'plan', event: e, planId,
        reapproval: e.payload.reapproval ? { reason: e.payload.reason ?? null } : null,
      });
      return;
    }
    items.push({ kind: 'event', event: e });
  });
  return items;
}

/** Number of steps in a collapsed tool row: one per call (a result without its call counts too). */
export function toolStepCount(events: AgentEvent[]): number {
  const calls = new Set<string>();
  let orphans = 0;
  events.forEach((e) => {
    if (e.type === 'tool_call') calls.add(String(e.payload?.id));
  });
  events.forEach((e) => {
    if (e.type === 'tool_result' && !calls.has(String(e.payload?.id))) orphans += 1;
  });
  return calls.size + orphans;
}

/**
 * True while the agent is still working: the last user message (or approval) came after the
 * last `done`/`error`/`result` or cancelled/superseded plan.
 */
export function isWorking(events: AgentEvent[]): boolean {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i];
    if (e.type === 'done' || e.type === 'error' || e.type === 'result') return false;
    if (e.type === 'user_message') return true;
    if (e.type === 'status' && (e.payload?.state === 'cancelled' || e.payload?.state === 'superseded')) return false;
    if (e.type === 'status' && (e.payload?.state === 'approved' || e.payload?.state === 'running')) return true;
    if (e.type === 'plan') return false;
  }
  return false;
}

/** "1.2 MB" style size. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

const ATTACHMENT = /\.(csv|xlsx|txt|json)$/i;

/** The types the server accepts: CSV, XLSX, TXT and JSON. */
export function isAllowedAttachment(name: string): boolean {
  return ATTACHMENT.test(name);
}
